import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../ReportMetrics.module.scss';
import detailStyles from '../widgets/Detail.module.scss';
import { KPI, numeric } from '../intelligence';
import {
  DEPENDENCY_TONE,
  openActionsInArea,
  conspicuousDependencies,
  dependenciesByPhase,
  dependencyRows,
} from '../detail';
import { criticalityColors } from '../reportTheme';
import type { ReportViewProps } from './viewProps';
import { pct, scoreStatus, tileDelta } from './tiles';
import ChartCard from '../widgets/ChartCard';
import MetricTile from '../widgets/MetricTile';
import DataTable from '../widgets/DataTable';
import StatusBadge from '../widgets/StatusBadge';
import InlineBar from '../widgets/InlineBar';
import DependencyNetwork from '../widgets/DependencyNetwork';
import ActionsHint from '../widgets/ActionsHint';

/**
 * Dependencies (Detailmasken §5): which dependencies exist, which are violated
 * or waiting on a late predecessor, and which deliverables that holds up. No
 * risk score and no critical-path simulation in V1 - violations, late
 * predecessors and their direct successors.
 */
const Dependencies: React.FC<ReportViewProps> = ({
  actionPoints,
  historyIndex,
  dependencyGraph,
  index,
  phaseNames,
  openTab,
  openObject,
}) => {
  const { t } = useTranslation(['templates', 'gantt']);

  const rows = useMemo(() => dependencyRows(dependencyGraph), [dependencyGraph]);
  const conspicuous = useMemo(() => conspicuousDependencies(rows), [rows]);
  const byPhase = useMemo(() => dependenciesByPhase(rows, phaseNames), [rows, phaseNames]);

  const violated = rows.filter((r) => r.state === 'VIOLATED').length;
  const late = rows.filter((r) => r.state === 'PREDECESSOR_LATE').length;
  const predecessorsLate = new Set(
    rows.filter((r) => r.edge.predecessorDaysOverdue != null).map((r) => r.edge.from),
  ).size;
  const affectedSuccessors = new Set(conspicuous.map((r) => r.edge.to)).size;

  const score = numeric(index, KPI.dependencyScore);
  const dependencyActions = openActionsInArea(actionPoints, 'dependencies').length;

  return (
    <div className={styles.dashboard}>
      <div className={styles.tileRow}>
        <MetricTile
          label={t('report.kpi.dependencyScore.label')}
          value={pct(score)}
          delta={tileDelta(t, historyIndex, KPI.dependencyScore, score)}
          status={scoreStatus(t, score)}
        />
        <MetricTile
          label={t('report.detail.dependencies.violations')}
          value={`${violated}`}
          sub={t('report.detail.dependencies.rate', { value: pct(numeric(index, KPI.hardDependencyViolationRate)) })}
          status={violated > 0 ? { tone: 'critical', label: t('report.severity.CRITICAL') } : undefined}
        />
        <MetricTile
          label={t('report.detail.dependencies.latePredecessors')}
          value={`${predecessorsLate}`}
          sub={t('report.detail.dependencies.rate', { value: pct(numeric(index, KPI.overduePredecessorRate)) })}
          status={predecessorsLate > 0 ? { tone: 'warning', label: t('report.detail.attention') } : undefined}
        />
        <MetricTile
          label={t('report.detail.dependencies.affectedSuccessors')}
          value={`${affectedSuccessors}`}
          status={affectedSuccessors > 0 ? { tone: 'warning', label: t('report.detail.attention') } : undefined}
        />
        <MetricTile
          label={t('report.detail.dependencies.total')}
          value={`${rows.length}`}
          footer={
            <span className={detailStyles.tileSub}>
              {t('report.detail.dependencies.distribution', {
                ok: rows.length - violated - late,
                violated,
                late,
              })}
            </span>
          }
        />
      </div>

      <div className={detailStyles.halves}>
        <div>
          <ChartCard
            title={t('report.dependencyNetwork.title')}
            subtitle={t('report.dependencyNetwork.subtitle')}
            fitContent
          >
            <DependencyNetwork graph={dependencyGraph} phaseNames={phaseNames} />
          </ChartCard>
        </div>

        <ChartCard
          title={t('report.detail.dependencies.byPhase')}
          subtitle={t('report.detail.dependencies.byPhaseHint')}
          fitContent
        >
          <DataTable
            rows={byPhase}
            rowKey={(row) => row.id}
            empty={t('report.dependencyNetwork.empty')}
            columns={[
              { key: 'phase', header: t('report.detail.col.phase'), render: (row) => row.name },
              { key: 'total', header: t('report.detail.dependencies.totalShort'), align: 'right', render: (row) => row.total },
              {
                key: 'violated',
                header: t('report.detail.dependencies.state.VIOLATED'),
                align: 'right',
                render: (row) => <span className={row.violated > 0 ? detailStyles.critical : undefined}>{row.violated}</span>,
              },
              {
                key: 'late',
                header: t('report.detail.dependencies.state.PREDECESSOR_LATE'),
                align: 'right',
                render: (row) => <span className={row.late > 0 ? detailStyles.warning : undefined}>{row.late}</span>,
              },
              {
                key: 'ok',
                header: t('report.detail.dependencies.okShare'),
                render: (row) => (
                  <InlineBar value={row.total > 0 ? (row.ok / row.total) * 100 : undefined} tone="good" />
                ),
              },
            ]}
          />
        </ChartCard>
      </div>

      <ChartCard
        title={t('report.detail.dependencies.conspicuous')}
        subtitle={t('report.detail.dependencies.conspicuousHint')}
        fitContent
      >
        <DataTable
          rows={conspicuous}
          rowKey={(row) => row.id}
          limit={5}
          empty={t('report.detail.dependencies.noConspicuous')}
          onRowClick={(row) => row.edge.to != null && openObject?.({ kind: 'deliverable', id: row.edge.to })}
          columns={[
            { key: 'from', header: t('report.detail.col.predecessor'), render: (row) => row.predecessor },
            { key: 'to', header: t('report.detail.col.successor'), render: (row) => row.successor },
            {
              key: 'type',
              header: t('report.detail.col.type'),
              render: (row) => t(`report.detail.dependencies.type.${row.edge.type}`, row.edge.type ?? ''),
            },
            {
              key: 'status',
              header: t('report.detail.col.status'),
              render: (row) => (
                <StatusBadge tone={DEPENDENCY_TONE[row.state]} label={t(`report.detail.dependencies.state.${row.state}`)} />
              ),
            },
            {
              key: 'delay',
              header: t('report.detail.col.delay'),
              render: (row) =>
                row.edge.predecessorDaysOverdue != null ? (
                  <span className={detailStyles.critical}>
                    {t('report.detail.daysLate', { count: row.edge.predecessorDaysOverdue })}
                  </span>
                ) : (
                  '—'
                ),
            },
            {
              key: 'impact',
              header: t('report.detail.dependencies.successorCriticality'),
              render: (row) =>
                row.successorCriticality ? (
                  <span style={{ color: criticalityColors[row.successorCriticality], fontWeight: 600 }}>
                    {t(`report.criticality.${row.successorCriticality}`)}
                  </span>
                ) : (
                  '—'
                ),
            },
          ]}
        />
      </ChartCard>

      <ActionsHint
        text={t('report.detail.actionsHint.dependencies', { count: dependencyActions })}
        onOpen={() => openTab?.('actions')}
      />
    </div>
  );
};

export default Dependencies;
